import { useCallback, useState, useRef, useEffect, useMemo } from 'react'

export function TimelineSlider({
  startDate,
  endDate,
  onDateRangeChange,
  minDate,
  maxDate,
  sortedPoints,
  allPoints,
  currentPoint,
  onPointNavigate
}) {
  const [isDragging, setIsDragging] = useState(null)
  const [dragPosition, setDragPosition] = useState(null)
  const sliderRef = useRef(null)

  const totalDays = useMemo(() => {
    if (!minDate || !maxDate) return 1
    return Math.max(
      1,
      Math.floor(
        (maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)
      )
    )
  }, [minDate, maxDate])

  const isSingleDay = totalDays <= 1

  const startDay =
    minDate && startDate
      ? Math.floor(
          (startDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)
        )
      : 0
  const endDay =
    minDate && endDate
      ? Math.floor(
          (endDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24)
        )
      : 0

  const startPercentage =
    totalDays > 1 ? Math.max(0, Math.min(100, (startDay / totalDays) * 100)) : 0
  const endPercentage =
    totalDays > 1 ? Math.max(0, Math.min(100, (endDay / totalDays) * 100)) : 100

  const displayStartPercentage =
    isDragging === 'start' && dragPosition !== null
      ? dragPosition
      : startPercentage
  const displayEndPercentage =
    isDragging === 'end' && dragPosition !== null ? dragPosition : endPercentage

  const handleMouseDown = (handle) => {
    if (isSingleDay) return
    setIsDragging(handle)
    setDragPosition(handle === 'start' ? startPercentage : endPercentage)
  }

  const handleMouseUp = useCallback(() => {
    if (isDragging && dragPosition !== null && minDate) {
      const newDay = Math.round((dragPosition / 100) * totalDays)
      const newDate = new Date(minDate.getTime() + newDay * 24 * 60 * 60 * 1000)

      if (isDragging === 'start' && newDate < endDate) {
        onDateRangeChange(newDate, endDate)
      } else if (isDragging === 'end' && newDate > startDate) {
        onDateRangeChange(startDate, newDate)
      }
    }
    setIsDragging(null)
    setDragPosition(null)
  }, [
    isDragging,
    dragPosition,
    minDate,
    totalDays,
    startDate,
    endDate,
    onDateRangeChange
  ])

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !sliderRef.current || isSingleDay) return

      const rect = sliderRef.current.getBoundingClientRect()
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width))
      const newPercentage = (x / rect.width) * 100

      if (isDragging === 'start') {
        setDragPosition(Math.max(0, Math.min(newPercentage, endPercentage - 1)))
      } else if (isDragging === 'end') {
        setDragPosition(
          Math.min(100, Math.max(newPercentage, startPercentage + 1))
        )
      }
    },
    [isDragging, isSingleDay, startPercentage, endPercentage]
  )

  const handleClick = (e) => {
    if (!sliderRef.current || isSingleDay || !minDate || isDragging) return

    const rect = sliderRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const newPercentage = (x / rect.width) * 100
    const newDay = Math.round((newPercentage / 100) * totalDays)
    const newDate = new Date(minDate.getTime() + newDay * 24 * 60 * 60 * 1000)

    const distToStart = Math.abs(newPercentage - startPercentage)
    const distToEnd = Math.abs(newPercentage - endPercentage)

    if (distToStart < distToEnd) {
      if (newDate < endDate) onDateRangeChange(newDate, endDate)
    } else {
      if (newDate > startDate) onDateRangeChange(startDate, newDate)
    }
  }

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
        window.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [handleMouseMove, handleMouseUp, isDragging])

  const getDragDate = useCallback(
    (percentage) => {
      if (!minDate) return null
      const dayOffset = Math.round((percentage / 100) * totalDays)
      return new Date(minDate.getTime() + dayOffset * 24 * 60 * 60 * 1000)
    },
    [minDate, totalDays]
  )

  const ticks = 10

  const panelClasses = [
    'rounded-lg border backdrop-blur-xl shadow-lg',
    'bg-white/90 dark:bg-[#0B0F14]/90',
    'border-slate-200/70 dark:border-[#0EA5E9]/20'
  ].join(' ')

  if (!minDate || !maxDate || !startDate || !endDate) {
    return (
      <div className={panelClasses}>
        <div className="px-6 py-3">
          <div className="text-center">
            <div className="text-slate-500 dark:text-gray-400 text-xs uppercase tracking-wide">
              Loading timeline...
            </div>
          </div>
        </div>
      </div>
    )
  }

  const displayStartDate =
    isDragging === 'start' && dragPosition !== null
      ? getDragDate(dragPosition)
      : startDate
  const displayEndDate =
    isDragging === 'end' && dragPosition !== null
      ? getDragDate(dragPosition)
      : endDate

  return (
    <div className={panelClasses}>
      <div className="px-6 py-4">
        <div className="text-center mb-3">
          <div className="text-slate-500 dark:text-gray-400 text-xs uppercase tracking-wide mb-1">
            Date Range Filter {isSingleDay && '(Single Day - Disabled)'}
          </div>
          <div className="text-slate-800 dark:text-gray-100 text-sm font-light">
            {displayStartDate?.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            })}{' '}
            -{' '}
            {displayEndDate?.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </div>
        </div>

        <div className="relative">
          <div
            ref={sliderRef}
            onClick={handleClick}
            className={[
              'relative h-2 rounded-full border',
              'bg-slate-200 dark:bg-gray-800',
              'border-slate-300 dark:border-[#0EA5E9]/20',
              isSingleDay ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            ].join(' ')}
          >
            <div
              className="absolute top-0 h-full bg-[#0EA5E9] rounded-full"
              style={{
                left: `${displayStartPercentage}%`,
                width: `${displayEndPercentage - displayStartPercentage}%`,
                transition: isDragging
                  ? 'none'
                  : 'left 0.15s ease-out, width 0.15s ease-out'
              }}
            />

            {!isSingleDay && (
              <>
                <div
                  onMouseDown={() => handleMouseDown('start')}
                  className={[
                    'absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full cursor-grab active:cursor-grabbing z-10',
                    'bg-[#0EA5E9] border-2 border-white dark:border-gray-900',
                    'shadow-lg',
                    isDragging === 'start' ? 'scale-125' : 'hover:scale-110'
                  ].join(' ')}
                  style={{
                    left: `${displayStartPercentage}%`,
                    transform: 'translate(-50%, -50%)',
                    transition:
                      isDragging === 'start'
                        ? 'none'
                        : 'left 0.15s ease-out, transform 0.1s ease-out'
                  }}
                />

                <div
                  onMouseDown={() => handleMouseDown('end')}
                  className={[
                    'absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full cursor-grab active:cursor-grabbing z-10',
                    'bg-[#0EA5E9] border-2 border-white dark:border-gray-900',
                    'shadow-lg',
                    isDragging === 'end' ? 'scale-125' : 'hover:scale-110'
                  ].join(' ')}
                  style={{
                    left: `${displayEndPercentage}%`,
                    transform: 'translate(-50%, -50%)',
                    transition:
                      isDragging === 'end'
                        ? 'none'
                        : 'left 0.15s ease-out, transform 0.1s ease-out'
                  }}
                />
              </>
            )}
          </div>

          {!isSingleDay && (
            <div className="flex justify-between mt-2">
              {Array.from({ length: ticks + 1 }).map((_, i) => {
                const percentage = (i / ticks) * 100
                const dayOffset = Math.floor((percentage / 100) * totalDays)
                const tickDate = new Date(
                  minDate.getTime() + dayOffset * 24 * 60 * 60 * 1000
                )

                return (
                  <div key={i} className="relative flex flex-col items-center">
                    <div className="w-px h-2 bg-slate-300 dark:bg-[#0EA5E9]/30" />
                    {i % 2 === 0 && (
                      <span className="text-[10px] text-slate-400 dark:text-gray-500 mt-1">
                        {tickDate.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TimelineSlider
